import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUrl } from '../config/api';
import { AuthContext } from './AuthContext';
import LoanDetails from '../components/Forms/LoanDetails';
import ClientDetailsModal from '../components/ClientDetailsModal';
import RepaymentDetailsModal from '../components/RepaymentDetailsModal';
import OpenFullPageLink from '../components/OpenFullPageLink';

// One place any page can open the loan, client or repayment details from, as
// a quick look over the current page. Staff get an "Open full page" link to the page
// that holds the record; a client only ever sees their own loans.
const DetailsModalContext = createContext({
  openLoan: () => {},
  openClient: () => {},
  openRepayment: () => {},
  closeDetails: () => {},
  isClientUser: false,
  canViewClients: false,
  canViewLoans: false,
  canViewRepayments: false,
});

export const useDetailsModal = () => useContext(DetailsModalContext);

const permsOf = (user) => (Array.isArray(user?.permissions) ? user.permissions : []);

export const DetailsModalProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [loan, setLoan] = useState(null);
  const [client, setClient] = useState(null); // { id, name }
  const [repayment, setRepayment] = useState(null); // { id, row }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Which is on top when more than one is open (a client opened from a loan,
  // a loan from a repayment...): the last opened is last in the list.
  const [order, setOrder] = useState([]);
  const bringToTop = useCallback((kind) => setOrder((o) => [...o.filter((k) => k !== kind), kind]), []);
  const requestRef = useRef(0);

  const perms = permsOf(user);
  const isClientUser = !perms.includes('*') && (perms.includes('SignDocuments') || perms.includes('ViewProfile'));
  const canViewLoans = !isClientUser && (perms.includes('*') || perms.includes('ViewLoans') || perms.includes('ManageLoans'));
  const canViewClients = !isClientUser && (perms.includes('*') || perms.includes('ManageClients') || perms.includes('ViewLoans') || perms.includes('ManageLoans'));
  const canOpenClientsPage = !isClientUser && (perms.includes('*') || perms.includes('ManageClients'));
  // The Repayments page: staff who can see loans or take payments.
  const canOpenRepaymentsPage = !isClientUser && (perms.includes('*') || perms.includes('ViewLoans') || perms.includes('ManageLoans') || perms.includes('EnterRepayment'));
  // A client opens their own repayments (the API refuses anyone else's).
  const canViewRepayments = isClientUser || canOpenRepaymentsPage;

  const closeDetails = useCallback(() => {
    requestRef.current += 1;
    setLoan(null);
    setClient(null);
    setRepayment(null);
    setLoading(false);
    setError('');
  }, []);

  // Moving to another page (or refreshing this one) closes the quick look.
  useEffect(() => {
    closeDetails();
  }, [location.key, closeDetails]);

  // loanOrId: a loan object already on screen, or just its id.
  const openLoan = useCallback(async (loanOrId, extra = {}) => {
    const given = loanOrId && typeof loanOrId === 'object' ? loanOrId : null;
    const id = given ? given.id : loanOrId;
    if (!id) return;
    const req = ++requestRef.current;
    setError('');
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    try {
      setLoading(true);
      let full = null;
      if (canViewLoans) {
        full = (await axios.get(apiUrl(`/api/loans/${id}`), { headers })).data;
      } else if (isClientUser) {
        const mine = (await axios.get(apiUrl('/api/loans/my-loans'), { headers })).data;
        full = (Array.isArray(mine) ? mine : []).find((l) => String(l?.id) === String(id)) || null;
      }
      if (req !== requestRef.current) return;
      const merged = full ? { ...(given || {}), ...full, ...extra } : (given ? { ...given, ...extra } : null);
      if (!merged) {
        setError('That loan could not be opened.');
        return;
      }
      setLoan(merged);
      bringToTop('loan');
    } catch {
      if (req !== requestRef.current) return;
      if (given) { setLoan({ ...given, ...extra }); bringToTop('loan'); }
      else setError('That loan could not be opened.');
    } finally {
      if (req === requestRef.current) setLoading(false);
    }
  }, [canViewLoans, isClientUser, bringToTop]);

  const openClient = useCallback((clientId, name = '') => {
    if (!clientId || !canViewClients) return;
    requestRef.current += 1;
    setLoading(false);
    setError('');
    setClient({ id: clientId, name });
    bringToTop('client');
  }, [canViewClients, bringToTop]);

  // idOrRow: a repayment row already on screen (shown while it loads), or just its id.
  const openRepayment = useCallback((idOrRow) => {
    const row = idOrRow && typeof idOrRow === 'object' ? idOrRow : null;
    const id = row ? (row.id ?? row.repayment_id) : idOrRow;
    if (!id || !canViewRepayments) return;
    requestRef.current += 1;
    setLoading(false);
    setError('');
    setRepayment({ id, row });
    bringToTop('repayment');
  }, [canViewRepayments, bringToTop]);

  const value = useMemo(() => ({
    openLoan, openClient, openRepayment, closeDetails, isClientUser, canViewClients, canViewLoans, canViewRepayments,
  }), [openLoan, openClient, openRepayment, closeDetails, isClientUser, canViewClients, canViewLoans, canViewRepayments]);

  const goTo = (path) => {
    closeDetails();
    navigate(path, { state: { refresh: Date.now() } });
  };

  const loanView = loan && (
    <LoanDetails
      key={`loan-${loan.id}`}
      loan={loan}
      onClose={() => setLoan(null)}
      headerExtra={canViewLoans
        ? <OpenFullPageLink onClick={() => goTo(`/manage-loans?loan=${loan.id}`)} />
        : null}
    />
  );
  const clientView = client && (
    <ClientDetailsModal
      key={`client-${client.id}`}
      clientId={client.id}
      name={client.name}
      onClose={() => setClient(null)}
      onOpenFullPage={canOpenClientsPage ? () => goTo(`/manage-clients?client=${client.id}`) : null}
    />
  );
  const repaymentView = repayment && (
    <RepaymentDetailsModal
      key={`repayment-${repayment.id}`}
      repaymentId={repayment.id}
      initial={repayment.row}
      showClient={canViewClients}
      onClose={() => setRepayment(null)}
      onOpenFullPage={canOpenRepaymentsPage ? (loanId) => goTo(`/repayments?loan_id=${loanId}`) : null}
    />
  );
  // Keyed, so bringing one to the front reorders them without reloading any.
  const views = { loan: loanView, client: clientView, repayment: repaymentView };
  const stacked = [...['loan', 'client', 'repayment'].filter((k) => !order.includes(k)), ...order]
    .map((k) => views[k])
    .filter(Boolean);

  return (
    <DetailsModalContext.Provider value={value}>
      {children}

      {loading && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/20" role="status" aria-live="polite">
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-white dark:bg-slate-800 shadow-xl text-sm text-gray-700 dark:text-slate-200">
            <span className="inline-block w-4 h-4 rounded-full border-2 border-blue-600 border-b-transparent animate-spin" />
            Loading…
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="fixed bottom-4 right-4 z-40 flex items-center gap-3 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-red-800 dark:bg-red-900/40 dark:border-red-700 dark:text-red-200 shadow-lg text-sm" role="alert">
          {error}
          <button type="button" onClick={() => setError('')} className="font-bold" aria-label="Dismiss">&times;</button>
        </div>
      )}

      {stacked}
    </DetailsModalContext.Provider>
  );
};

export default DetailsModalContext;
